import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Dropdown,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip as AntTooltip,
  Typography
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import { DownloadOutlined, DownOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { analyticsApi } from 'api/analytics'
import {
  IAnalyticsDay,
  IAnalyticsFunnelStepStats,
  IAnalyticsFunnelUser,
  IAnalyticsFunnelUsers,
  IAnalyticsFunnelUsersQuery,
  IAnalyticsOverview,
  IAnalyticsPlatformStats,
  IAnalyticsPlatformVisits,
  IAnalyticsSourceDay,
  IAnalyticsSourceStats,
  IAnalyticsSourceVisits,
  TAnalyticsFunnelStep,
  TAnalyticsPersonKind,
  TAnalyticsPlatformFilter,
  TAnalyticsSourceKind,
  TAnalyticsStage,
  TClientPlatform
} from 'models/analytics'
import { showMessage } from 'shared/utils/showMessage'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { getDeclination } from 'shared/utils/getDeclination'
import { superAdminRoutes } from 'shared/constants/routes'
import { multiOrderTags } from 'shared/constants/orders'
import { platformColors, platformLabels } from 'shared/constants/platforms'
import { SectionTitle } from 'components/ui'
import styles from './Analytics.module.scss'
import 'dayjs/locale/ru'

type TDateRange = [Dayjs, Dayjs]

const PERIOD_FORMAT = 'YYYY-MM-DD'
const PAGE_SIZE = 20
const VIEWED_PREVIEW = 30
const DEFAULT_CHART_SOURCES = 5

const sourceChartColors = [
  '#2f75ff',
  '#FF9433',
  '#8b5cf6',
  '#22c55e',
  '#ef4444',
  '#06b6d4',
  '#eab308',
  '#ec4899'
]

type TSourceCounter = Exclude<keyof IAnalyticsSourceDay, 'date'>

// Новые посетители есть только у рекламных ссылок, у шагов воронки их нет
type TStepCounter = Exclude<TSourceCounter, 'new_visitors'>

const sourceCounter = (step: TAnalyticsFunnelStep): TStepCounter =>
  step === 'visit' ? 'visitors' : step

const stepLabels: Record<TAnalyticsFunnelStep, string> = {
  visit: 'Зашли',
  product_view: 'Смотрели товар',
  add_to_cart: 'Добавили в корзину',
  checkout_start: 'Начали оформление',
  order: 'Оформили заказ',
  paid: 'Оплатили заказ'
}

const stepOptions = (Object.keys(stepLabels) as TAnalyticsFunnelStep[]).map(
  (step) => ({ value: step, label: stepLabels[step] })
)

const stepMenuItems = stepOptions.map(({ value, label }) => ({ key: value, label }))

const steps = stepOptions.map((option) => option.value)
const lastStep = steps[steps.length - 1]

const stageOptions: {
  value: TAnalyticsStage
  label: string
  countLabel: string
  className: string
  file: string
}[] = [
  {
    value: 'stopped',
    label: 'Остановились на этапе',
    countLabel: 'Остановились',
    className: styles.stageStopped,
    file: '_ostanovilis'
  },
  {
    value: 'passed',
    label: 'Прошли на следующий этап',
    countLabel: 'Прошли дальше',
    className: styles.stagePassed,
    file: '_proshli_dalshe'
  },
  {
    value: 'all',
    label: 'Все',
    countLabel: 'Всего на шаге',
    className: styles.stageAll,
    file: ''
  }
]

type TSourceChartType = 'line' | 'bar'

const sourceChartTypeLabels: Record<TSourceChartType, string> = {
  line: 'Линии',
  bar: 'Столбцы'
}

const sourceChartTypeItems = (Object.keys(sourceChartTypeLabels) as TSourceChartType[]).map(
  (key) => ({ key, label: sourceChartTypeLabels[key] })
)

const uniqueSourcesHint = [
  'Каждый человек считается только один раз за период',
  'Повторные действия не идут в воронку',
  'Источник — откуда пришёл впервые'
]

const visitSourcesHint = [
  'Каждый вход считается новым (спустя 30 минут перерыва)',
  'У каждого входа свой источник и воронка'
]

const sourceKindLabels: Record<TAnalyticsSourceKind, string> = {
  utm: 'реклама',
  referrer: 'переход',
  app: 'напрямую',
  direct: 'напрямую',
  ad_link: 'наша ссылка'
}

const sourceKindColors: Record<TAnalyticsSourceKind, string> = {
  utm: 'gold',
  referrer: 'blue',
  app: 'purple',
  direct: 'default',
  ad_link: 'green'
}

const adLinkCounterLabels: Record<TSourceCounter, string> = {
  visitors: 'Входы',
  new_visitors: 'Новые посетители',
  product_view: stepLabels.product_view,
  add_to_cart: stepLabels.add_to_cart,
  checkout_start: stepLabels.checkout_start,
  order: stepLabels.order,
  paid: stepLabels.paid
}

const adLinkCounterItems = (Object.keys(adLinkCounterLabels) as TSourceCounter[]).map(
  (key) => ({ key, label: adLinkCounterLabels[key] })
)

const adLinksHint = [
  'Каждый вход по ссылке засчитывается ей, даже если человек уже бывал у нас — с поиска, рекламы или напрямую',
  'Новые посетители — входы, до которых человек ни разу не заходил ни на сайт, ни в приложение',
  'Без согласия на cookie посетитель узнаётся только в пределах суток, поэтому новых бывает больше, чем на самом деле'
]

type TPlatformOption = TAnalyticsPlatformFilter | 'all'

const platformOptions: { value: TPlatformOption; label: string }[] = [
  { value: 'all', label: 'Сайт и приложение' },
  { value: 'web', label: 'Сайт' },
  { value: 'app', label: 'Приложение' },
  { value: 'ios', label: 'Приложение iOS' },
  { value: 'android', label: 'Приложение Android' }
]

const rangePresets: { label: string; value: TDateRange }[] = [
  { label: '7 дней', value: [dayjs().subtract(6, 'day'), dayjs()] },
  { label: '30 дней', value: [dayjs().subtract(29, 'day'), dayjs()] },
  { label: '90 дней', value: [dayjs().subtract(89, 'day'), dayjs()] },
  { label: 'Этот месяц', value: [dayjs().startOf('month'), dayjs()] }
]

const formatNumber = (value: number) => value.toLocaleString('ru-RU')

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

const formatDuration = (ms: number) => {
  if (ms >= DAY_MS) {
    const days = Math.floor(ms / DAY_MS)
    return `${days} ${getDeclination(days % 100, ['дней', 'день', 'дня'])}`
  }
  if (ms >= HOUR_MS) {
    const hours = Math.floor(ms / HOUR_MS)
    return `${hours} ${getDeclination(hours % 100, ['часов', 'час', 'часа'])}`
  }
  if (ms >= MINUTE_MS) {
    const minutes = Math.floor(ms / MINUTE_MS)
    return `${minutes} ${getDeclination(minutes % 100, ['минут', 'минута', 'минуты'])}`
  }
  return 'меньше минуты'
}

const personKinds: TAnalyticsPersonKind[] = ['account', 'contact', 'anonymous', 'bot']

const personKindLabels: Record<TAnalyticsPersonKind, string> = {
  account: 'с аккаунтом',
  contact: 'оставили контакт',
  anonymous: 'без контактов',
  bot: 'роботы'
}

const personKindTags: Record<
  Exclude<TAnalyticsPersonKind, 'account'>,
  { label: string; color: string }
> = {
  contact: { label: 'гость с контактом', color: 'cyan' },
  anonymous: { label: 'без контактов', color: 'default' },
  bot: { label: 'робот', color: 'red' }
}

const stepKindCounts = (
  item: IAnalyticsFunnelStepStats
): Record<TAnalyticsPersonKind, number> => ({
  account: item.with_account,
  contact: item.with_contact,
  anonymous: item.without_contact,
  bot: item.bots
})

const stepBreakdown = (item: IAnalyticsFunnelStepStats) => {
  const counts = stepKindCounts(item)
  return personKinds
    .filter((kind) => kind === 'account' || counts[kind] > 0)
    .map((kind) => `${personKindLabels[kind]} ${formatNumber(counts[kind])}`)
    .join(' · ')
}

const usersSummary = (
  users: IAnalyticsFunnelUsers,
  stage: TAnalyticsStage,
  showAnonymous: boolean
) => {
  const label = stageOptions.find((option) => option.value === stage)?.countLabel ?? ''
  const isListed = (kind: TAnalyticsPersonKind) =>
    showAnonymous || kind === 'account' || kind === 'contact'
  const describe = (kinds: TAnalyticsPersonKind[]) =>
    kinds
      .map((kind) => ({ kind, count: users.kinds[kind][stage] ?? 0 }))
      .filter(({ count }) => count > 0)
      .map(({ kind, count }) => `${personKindLabels[kind]} ${formatNumber(count)}`)
      .join(' · ')
  const listed = describe(personKinds.filter(isListed))
  const hidden = describe(personKinds.filter((kind) => !isListed(kind)))
  return [
    `${label} — ${formatNumber(users[stage] ?? users.total)}${listed ? `: ${listed}` : ''}.`,
    hidden ? `Не в списке: ${hidden} — их покажет «Показывать анонимных».` : ''
  ]
    .filter(Boolean)
    .join(' ')
}

// Прошедшим — сколько шли до следующего шага, остальным — сколько прошло с шага
const formatStageTime = (user: IAnalyticsFunnelUser) =>
  formatDuration(
    dayjs(user.next_reached_at ?? undefined).diff(dayjs(user.reached_at))
  )

const SKIPPED_HINT =
  'Своего действия на этом шаге в этом круге нет — шаг засчитан по следующему. Например, корзину собрали раньше или на другом устройстве, а в этот раз сразу оформили заказ.'
const LOOP_HINT =
  'Количество воронок, которые проходит покупатель за выбранный период. После оплаченного заказа воронка начинает считаться заново до следующего оплаченного заказа'
const STAGE_TIME_HINT =
  'Остановились — сколько прошло с шага до сейчас. Прошли дальше — сколько шли до следующего шага.'
const CART_STOPPED_HINT =
  'Добавили в корзину, но не начали оформление — по сегодняшний день, на сайте или в приложении. Только люди с контактами: с аккаунтом и гости, оставившие телефон или почту.'
const STAGES_HINT =
  'Остановились — дошли до шага, но не до следующего. Прошли — дошли до следующего по сегодняшний день, на сайте или в приложении. Все — и те и другие. Число на кнопке — ровно те, кто в списке.'
const BOTS_HINT =
  'Бот (робот) — программа, которая сама называет себя роботом: поисковики, проверки сайта, скрипты. Узнаём по подписи браузера или по признаку, что браузером управляет программа. Робот, который выдаёт себя за обычный браузер, считается человеком — так правило не задевает людей.'
const PLATFORM_VISITS_HINT =
  'Как в «Источниках» по входам: после 30 минут перерыва заход считается новым входом. Вход считается на платформе, с которой начался'
const ANONYMOUS_HINT =
  'Гости без контактов: не регистрировались на площадке или просто не вошли в аккаунт. Контакты хранятся только у тех кто дал согласие на обработку ПД'

// Источника с пустым названием не бывает
const ALL_SOURCES = ''

const Hint = ({ title }: { title: React.ReactNode }) => (
  <AntTooltip title={title}>
    {/* Клик по значку в строке таблицы не должен её раскрывать */}
    <QuestionCircleOutlined
      className={styles.hint}
      onClick={(event) => event.stopPropagation()}
    />
  </AntTooltip>
)

const BotsCheckbox = ({
  checked,
  onChange
}: {
  checked: boolean
  onChange: (checked: boolean) => void
}) => (
  <span className={styles.titleOption}>
    <Checkbox checked={checked} onChange={(event) => onChange(event.target.checked)}>
      Учитывать ботов
    </Checkbox>
    <Hint title={BOTS_HINT} />
  </span>
)

const formatPercent = (part: number, whole: number) => {
  if (!whole) {
    return '—'
  }
  const percent = (part / whole) * 100
  return `${percent.toLocaleString('ru-RU', {
    maximumFractionDigits: percent < 10 ? 1 : 0
  })}%`
}

type TChartDay = IAnalyticsDay & { label: string; without_account: number }

const dayChartOrder = ['paid_orders', 'paid_seller_orders', 'without_account', 'with_account']

const DayTooltip = ({
  active,
  day,
  label,
  showApp,
  superOrders
}: {
  active?: boolean
  day?: TChartDay
  label?: React.ReactNode
  showApp: boolean
  superOrders: boolean
}) => {
  if (!active || !day) {
    return null
  }
  const { web, ios, android, unknown } = superOrders
    ? day.paid_orders_by_platform
    : day.paid_seller_orders_by_platform
  const orderParts = [
    web && `с сайта ${formatNumber(web)}`,
    ios + android && `из приложения ${formatNumber(ios + android)}`,
    unknown && `без платформы ${formatNumber(unknown)}`
  ].filter(Boolean)
  return (
    <div className={styles.chartTooltip}>
      <strong>{label}</strong>
      <span className={styles.chartTooltipOrders}>
        {superOrders ? 'Супер-заказы' : 'Заказы'}:{' '}
        {formatNumber(superOrders ? day.paid_orders : day.paid_seller_orders)}
      </span>
      {orderParts.length > 1 && <span className={styles.muted}>{orderParts.join(' · ')}</span>}
      <span>Посетители: {formatNumber(day.visitors)}</span>
      <span className={styles.chartTooltipAccount}>
        С аккаунтом: {formatNumber(day.with_account)}
      </span>
      {showApp && <span>Из приложения: {formatNumber(day.app_visitors)}</span>}
    </div>
  )
}

const formatDateTime = (value: string | null) =>
  value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—'

const renderPlatformTags = (platforms: TClientPlatform[]) => (
  <div className={styles.tags}>
    {platforms.map((item) => (
      <Tag key={item} color={platformColors[item]}>
        {platformLabels[item]}
      </Tag>
    ))}
  </div>
)

const getPlatformColumns = (
  superOrders: boolean,
  visits: IAnalyticsPlatformVisits[] | undefined,
  isVisitsLoading: boolean
): ColumnsType<IAnalyticsPlatformStats> => [
  {
    title: 'Платформа',
    dataIndex: 'platform',
    render: (platform: TClientPlatform) => (
      <Tag color={platformColors[platform]}>{platformLabels[platform]}</Tag>
    )
  },
  {
    title: 'Посетители',
    dataIndex: 'visitors',
    align: 'right',
    render: formatNumber
  },
  {
    title: (
      <>
        Входы
        <Hint title={PLATFORM_VISITS_HINT} />
      </>
    ),
    key: 'visits',
    align: 'right',
    render: (_, row) => {
      if (isVisitsLoading) {
        return <Spin size='small' />
      }
      const item = visits?.find((visit) => visit.platform === row.platform)
      return item ? formatNumber(item.visits) : '—'
    }
  },
  {
    title: 'С аккаунтом',
    dataIndex: 'with_account',
    align: 'right',
    render: formatNumber
  },
  {
    title: superOrders ? 'Супер-заказы' : 'Заказы',
    dataIndex: superOrders ? 'orders' : 'seller_orders',
    align: 'right',
    render: formatNumber
  }
]

const sourceColumns: ColumnsType<IAnalyticsSourceStats> = [
  {
    title: 'Источник',
    dataIndex: 'source',
    render: (source: string, record) => (
      <span className={styles.source}>
        {source}
        {record.merged_sources && (
          <Hint
            title={
              <div className={styles.mergedList}>
                {record.merged_sources.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </div>
            }
          />
        )}
        <Tag color={sourceKindColors[record.kind]}>
          {sourceKindLabels[record.kind]}
        </Tag>
      </span>
    )
  },
  {
    title: stepLabels.visit,
    dataIndex: 'visitors',
    align: 'right',
    width: 90,
    sorter: (a, b) => a.visitors - b.visitors,
    render: formatNumber
  },
  {
    title: stepLabels.product_view,
    dataIndex: 'product_view',
    align: 'right',
    width: 100,
    sorter: (a, b) => a.product_view - b.product_view,
    render: formatNumber
  },
  {
    title: stepLabels.add_to_cart,
    dataIndex: 'add_to_cart',
    align: 'right',
    width: 110,
    sorter: (a, b) => a.add_to_cart - b.add_to_cart,
    render: formatNumber
  },
  {
    title: stepLabels.order,
    dataIndex: 'order',
    align: 'right',
    width: 100,
    sorter: (a, b) => a.order - b.order,
    render: formatNumber
  },
  {
    title: stepLabels.paid,
    dataIndex: 'paid',
    align: 'right',
    width: 100,
    sorter: (a, b) => a.paid - b.paid,
    render: formatNumber
  },
  {
    title: 'Конверсия в заказ',
    key: 'conversion',
    align: 'right',
    width: 100,
    sorter: (a, b) => a.order / (a.visitors || 1) - b.order / (b.visitors || 1),
    render: (_, record) => formatPercent(record.order, record.visitors)
  }
]

const [, visitsColumn, ...stepColumns] = sourceColumns

const adLinkColumns: ColumnsType<IAnalyticsSourceStats> = [
  { title: 'Ссылка', dataIndex: 'source' },
  { ...visitsColumn, title: adLinkCounterLabels.visitors },
  {
    title: adLinkCounterLabels.new_visitors,
    dataIndex: 'new_visitors',
    align: 'right',
    width: 100,
    sorter: (a, b) => (a.new_visitors ?? 0) - (b.new_visitors ?? 0),
    render: (value?: number) => formatNumber(value ?? 0)
  },
  ...stepColumns
]

// selected — источники на графике, null — все
const buildSourceChart = (
  sources: IAnalyticsSourceStats[],
  days: { date: string }[],
  selected: string[] | null,
  counter: TSourceCounter
) => {
  // Источники приходят по убыванию трафика: цвет берётся по этому месту, чтобы
  // не меняться при выборе других источников и смене шага
  const takenColors = new Set<number>()
  const series = sources
    .map((item, rank) => ({ item, rank }))
    .filter(({ item }) => !selected || selected.includes(item.source))
    .map(({ item, rank }, index) => {
      let color = rank % sourceChartColors.length
      while (takenColors.has(color) && takenColors.size < sourceChartColors.length) {
        color = (color + 1) % sourceChartColors.length
      }
      takenColors.add(color)
      return {
        key: `s${index}`,
        name: item.source,
        color: sourceChartColors[color],
        days: item.days
      }
    })

  const dayIndex = new Map(days.map((day, index) => [day.date, index]))
  const values = days.map(() => Object.fromEntries(series.map((item) => [item.key, 0])))
  for (const item of series) {
    for (const day of item.days) {
      const index = dayIndex.get(day.date)
      if (index !== undefined) {
        values[index][item.key] += day[counter] ?? 0
      }
    }
  }

  return {
    series,
    rows: days.map((day, index) => ({
      label: dayjs(day.date).format('DD.MM'),
      ...values[index]
    })),
    // recharts по умолчанию сортирует легенду и подсказку по алфавиту, а нужно по трафику
    order: (item: { dataKey?: unknown }) =>
      series.findIndex((entry) => entry.key === item.dataKey)
  }
}

const SourceChart = ({
  chart,
  type
}: {
  chart: ReturnType<typeof buildSourceChart>
  type: TSourceChartType
}) => (
  <div className={styles.chart}>
    <ResponsiveContainer width='100%' height='100%'>
      <ComposedChart data={chart.rows} margin={{ top: 8, right: 4, bottom: 8, left: 0 }}>
        <CartesianGrid stroke='#dfe8f5' vertical={false} />
        <XAxis
          dataKey='label'
          interval='preserveStartEnd'
          minTickGap={24}
          tick={{ fill: '#637aa0', fontSize: 12 }}
        />
        <YAxis allowDecimals={false} width={46} tick={{ fill: '#637aa0', fontSize: 12 }} />
        <Tooltip itemSorter={chart.order} />
        <Legend itemSorter={chart.order} />
        {chart.series.map((item) =>
          type === 'line' ? (
            <Line
              key={item.key}
              dataKey={item.key}
              name={item.name}
              type='monotone'
              stroke={item.color}
              strokeWidth={2}
              dot={false}
            />
          ) : (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.name}
              stackId='sources'
              fill={item.color}
            />
          )
        )}
      </ComposedChart>
    </ResponsiveContainer>
  </div>
)

const userColumns: ColumnsType<IAnalyticsFunnelUser> = [
  {
    title: 'Пользователь',
    dataIndex: 'name',
    width: 220,
    render: (name: string | null, user) => (
      <span className={styles.source}>
        {name || <span className={styles.muted}>Без имени</span>}
        {user.kind !== 'account' && (
          <Tag color={personKindTags[user.kind].color}>{personKindTags[user.kind].label}</Tag>
        )}
      </span>
    )
  },
  {
    title: 'Контакт',
    key: 'contact',
    width: 180,
    ellipsis: true,
    render: (_, user) => user.tel || user.email || '—'
  },
  {
    title: (
      <>
        Вхождений в воронку
        <Hint title={LOOP_HINT} />
      </>
    ),
    dataIndex: 'loop',
    width: 100,
    align: 'right'
  },
  {
    title: 'Рассылка',
    dataIndex: 'marketing_consent',
    width: 100,
    render: (consent: boolean) =>
      consent ? <Tag color='green'>согласен</Tag> : <Tag>нет</Tag>
  },
  {
    title: 'Источник',
    dataIndex: 'source',
    width: 160,
    ellipsis: true
  },
  {
    title: 'Платформа',
    dataIndex: 'platforms',
    width: 120,
    render: renderPlatformTags
  },
  {
    title: 'Последнее действие',
    dataIndex: 'last_at',
    width: 150,
    render: formatDateTime
  },
  {
    title: (
      <>
        Время на этапе
        <Hint title={STAGE_TIME_HINT} />
      </>
    ),
    key: 'stage_time',
    width: 140,
    render: (_, user) =>
      user.skipped ? (
        <span className={styles.muted}>
          —
          <Hint title={SKIPPED_HINT} />
        </span>
      ) : (
        formatStageTime(user)
      )
  },
  {
    title: 'Корзина',
    dataIndex: 'cart_total',
    width: 100,
    align: 'right',
    render: (total: number | null) => (total ? getNormalizedPrice(total) : '—')
  },
  {
    title: 'Заказы',
    dataIndex: 'orders',
    width: 80,
    align: 'right',
    render: (orders: number) => orders || <span className={styles.muted}>нет</span>
  }
]

const renderUserDetails = (user: IAnalyticsFunnelUser, step: TAnalyticsFunnelStep) => {
  const viewedRest = user.viewed_products.length - VIEWED_PREVIEW
  const nextStep = steps[steps.indexOf(step) + 1]
  return (
    <div className={styles.details}>
      <div className={styles.detailsBlock}>
        <span className={styles.detailsTitle}>Контакты</span>
        {user.tel && <a href={`tel:${user.tel.replace(/[^\d+]/g, '')}`}>{user.tel}</a>}
        {user.email && <a href={`mailto:${user.email}`}>{user.email}</a>}
        {user.kind === 'account' || user.kind === 'contact' ? (
          <span>
            Рассылка:{' '}
            {user.marketing_consent ? <Tag color='green'>согласен</Tag> : <Tag>нет</Tag>}
          </span>
        ) : (
          <span className={styles.muted}>нет</span>
        )}
        {/* У гостя в user_id сессия — карточки пользователя у него нет */}
        {user.kind === 'account' && (
          <Link
            to={superAdminRoutes.USERS_EDIT.replace(':id', user.user_id)}
            target='_blank'
            rel='noreferrer'
          >
            Карточка пользователя
          </Link>
        )}
        {user.kind === 'contact' && (
          <span className={styles.muted}>В аккаунт не входил — карточки нет</span>
        )}
      </div>

      <div className={styles.detailsBlock}>
        <span className={styles.detailsTitle}>Активность</span>
        <span>Первое действие: {formatDateTime(user.first_at)}</span>
        <span>Последнее действие: {formatDateTime(user.last_at)}</span>
        <span>Вхождение в воронку: {user.loop}</span>
        <span>
          Шаг «{stepLabels[step]}»:{' '}
          {user.skipped ? (
            <>
              засчитан по следующему
              <Hint title={SKIPPED_HINT} />
            </>
          ) : (
            formatDateTime(user.reached_at)
          )}
        </span>
        {user.next_reached_at && nextStep && (
          <span>
            Шаг «{stepLabels[nextStep]}»: {formatDateTime(user.next_reached_at)}
          </span>
        )}
        <span>Событий: {formatNumber(user.events)}</span>
        <span className={styles.source}>
          {user.source}
          <Tag color={sourceKindColors[user.source_kind]}>
            {sourceKindLabels[user.source_kind]}
          </Tag>
        </span>
        {renderPlatformTags(user.platforms)}
      </div>

      <div className={classNames(styles.detailsBlock, styles.detailsWide)}>
        <span className={styles.detailsTitle}>
          Заказы{user.order_list.length ? ` (${user.order_list.length})` : ''}
        </span>
        {user.order_list.length ? (
          user.order_list.map((order) => (
            <div key={order.multiorder_id} className={styles.order}>
              <div className={styles.orderHead}>
                <strong>№ {order.number}</strong>
                <span>{formatDateTime(order.created_at)}</span>
                <span>{getNormalizedPrice(order.sum)}</span>
                <Tag color={multiOrderTags[order.status]?.color}>
                  {multiOrderTags[order.status]?.label ?? order.status}
                </Tag>
                {order.platform && (
                  <Tag color={platformColors[order.platform]}>
                    {platformLabels[order.platform]}
                  </Tag>
                )}
              </div>
              <ul className={styles.list}>
                {order.products.map((product, index) => (
                  <li key={index}>
                    {product.name} × {product.count}
                  </li>
                ))}
              </ul>
            </div>
          ))
        ) : (
          <span className={styles.muted}>нет</span>
        )}
      </div>

      <div className={styles.detailsBlock}>
        <span className={styles.detailsTitle}>
          В корзине{user.cart_total ? ` на ${getNormalizedPrice(user.cart_total)}` : ''}
        </span>
        {user.cart_products.length ? (
          <ul className={styles.list}>
            {user.cart_products.map((name, index) => (
              <li key={index}>{name}</li>
            ))}
          </ul>
        ) : (
          <span className={styles.muted}>ничего</span>
        )}
      </div>

      <div className={styles.detailsBlock}>
        <span className={styles.detailsTitle}>
          Смотрел{user.viewed_products.length ? ` (${user.viewed_products.length})` : ''}
        </span>
        {user.viewed_products.length ? (
          <ul className={styles.list}>
            {user.viewed_products.slice(0, VIEWED_PREVIEW).map((name, index) => (
              <li key={index}>{name}</li>
            ))}
            {viewedRest > 0 && <li className={styles.muted}>и ещё {viewedRest}</li>}
          </ul>
        ) : (
          <span className={styles.muted}>ничего</span>
        )}
      </div>
    </div>
  )
}

export const ActionsAnalytics: React.FC = () => {
  const [range, setRange] = useState<TDateRange>(rangePresets[1].value)
  const [platform, setPlatform] = useState<TAnalyticsPlatformFilter>()
  const [overview, setOverview] = useState<IAnalyticsOverview | null>(null)
  const [isOverviewLoading, setIsOverviewLoading] = useState(true)
  const [isUsersOpen, setIsUsersOpen] = useState(false)
  const [step, setStep] = useState<TAnalyticsFunnelStep>('add_to_cart')
  const [stage, setStage] = useState<TAnalyticsStage>('stopped')
  const [page, setPage] = useState(1)
  const [users, setUsers] = useState<IAnalyticsFunnelUsers | null>(null)
  const [isUsersLoading, setIsUsersLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [sourceStep, setSourceStep] = useState<TAnalyticsFunnelStep>('visit')
  const [selectedSources, setSelectedSources] = useState<string[] | null>(null)
  const [sourceChartType, setSourceChartType] = useState<TSourceChartType>('line')
  const [adLinkCounter, setAdLinkCounter] = useState<TSourceCounter>('visitors')
  const [adLinkChartType, setAdLinkChartType] = useState<TSourceChartType>('line')
  const [isUniqueSources, setIsUniqueSources] = useState(false)
  const [sourceVisits, setSourceVisits] = useState<IAnalyticsSourceVisits | null>(null)
  const [isSourceVisitsLoading, setIsSourceVisitsLoading] = useState(false)
  const [sourceVisitsWithBots, setSourceVisitsWithBots] =
    useState<IAnalyticsSourceVisits | null>(null)
  const [isSourceVisitsWithBotsLoading, setIsSourceVisitsWithBotsLoading] = useState(false)
  const [includeChartBots, setIncludeChartBots] = useState(false)
  const [chartSuperOrders, setChartSuperOrders] = useState(true)
  const [includeFunnelBots, setIncludeFunnelBots] = useState(false)
  const [includeSourceBots, setIncludeSourceBots] = useState(false)
  const [overviewWithBots, setOverviewWithBots] = useState<IAnalyticsOverview | null>(null)
  const [isOverviewWithBotsLoading, setIsOverviewWithBotsLoading] = useState(false)
  const [showAnonymous, setShowAnonymous] = useState(false)
  const [funnelSource, setFunnelSource] = useState<string>()
  const [sourceOverview, setSourceOverview] = useState<IAnalyticsOverview | null>(null)
  const [isSourceOverviewLoading, setIsSourceOverviewLoading] = useState(false)
  const [usersSource, setUsersSource] = useState<string>()

  const from = range[0].format(PERIOD_FORMAT)
  const to = range[1].format(PERIOD_FORMAT)
  const isLastStep = step === lastStep
  // У последнего шага следующего нет — только все
  const usersStage: TAnalyticsStage = isLastStep ? 'all' : stage

  // С роботами нужна вторая сводка: график, воронка или уникальные источники с галочкой
  const needsOverviewWithBots =
    includeChartBots || includeFunnelBots || (isUniqueSources && includeSourceBots)

  const usersQuery = useMemo<IAnalyticsFunnelUsersQuery>(
    () => ({
      from,
      to,
      platform,
      step,
      stage: usersStage,
      include_bots: includeFunnelBots || undefined,
      include_anonymous: showAnonymous,
      source: usersSource
    }),
    [from, to, platform, step, usersStage, includeFunnelBots, showAnonymous, usersSource]
  )

  useEffect(() => {
    let isActual = true
    setIsOverviewLoading(true)

    analyticsApi
      .getOverview({ from, to, platform })
      .then((response) => isActual && setOverview(response.data))
      .catch(() => {
        if (!isActual) return
        setOverview(null)
        showMessage('error', 'Не удалось загрузить аналитику', '')
      })
      .finally(() => isActual && setIsOverviewLoading(false))

    return () => {
      isActual = false
    }
  }, [from, to, platform])

  useEffect(() => {
    if (!needsOverviewWithBots) {
      return
    }
    let isActual = true
    setOverviewWithBots(null)
    setIsOverviewWithBotsLoading(true)

    analyticsApi
      .getOverview({ from, to, platform, include_bots: true })
      .then((response) => isActual && setOverviewWithBots(response.data))
      .catch(() => {
        if (!isActual) return
        // Иначе под галочкой остались бы цифры без роботов
        setIncludeChartBots(false)
        setIncludeFunnelBots(false)
        setIncludeSourceBots(false)
        showMessage('error', 'Не удалось загрузить аналитику с роботами', '')
      })
      .finally(() => isActual && setIsOverviewWithBotsLoading(false))

    return () => {
      isActual = false
    }
  }, [from, to, platform, needsOverviewWithBots])

  useEffect(() => {
    if (!funnelSource) {
      return
    }
    let isActual = true
    setSourceOverview(null)
    setIsSourceOverviewLoading(true)

    analyticsApi
      .getOverview({
        from,
        to,
        platform,
        include_bots: includeFunnelBots || undefined,
        source: funnelSource
      })
      .then((response) => isActual && setSourceOverview(response.data))
      .catch(() => {
        if (!isActual) return
        // Иначе под выбранным источником осталась бы воронка по всем
        setFunnelSource(undefined)
        showMessage('error', 'Не удалось загрузить воронку по источнику', '')
      })
      .finally(() => isActual && setIsSourceOverviewLoading(false))

    return () => {
      isActual = false
    }
  }, [from, to, platform, includeFunnelBots, funnelSource])

  // Входы считаются дольше сводки — отдельным запросом, чтобы не задерживать остальные карточки.
  // Без роботов нужны всегда: из них колонка «Входы» в «Платформах»
  useEffect(() => {
    let isActual = true
    setSourceVisits(null)
    setIsSourceVisitsLoading(true)

    analyticsApi
      .getSourceVisits({ from, to, platform })
      .then((response) => isActual && setSourceVisits(response.data))
      .catch(() => {
        if (!isActual) return
        showMessage('error', 'Не удалось загрузить входы', '')
      })
      .finally(() => isActual && setIsSourceVisitsLoading(false))

    return () => {
      isActual = false
    }
  }, [from, to, platform])

  useEffect(() => {
    if (isUniqueSources || !includeSourceBots) {
      return
    }
    let isActual = true
    setSourceVisitsWithBots(null)
    setIsSourceVisitsWithBotsLoading(true)

    analyticsApi
      .getSourceVisits({ from, to, platform, include_bots: true })
      .then((response) => isActual && setSourceVisitsWithBots(response.data))
      .catch(() => {
        if (!isActual) return
        // Иначе под галочкой остались бы входы без роботов
        setIncludeSourceBots(false)
        showMessage('error', 'Не удалось загрузить источники по входам с роботами', '')
      })
      .finally(() => isActual && setIsSourceVisitsWithBotsLoading(false))

    return () => {
      isActual = false
    }
  }, [from, to, platform, isUniqueSources, includeSourceBots])

  useEffect(() => {
    if (!isUsersOpen) {
      return
    }
    let isActual = true
    setIsUsersLoading(true)

    analyticsApi
      .getFunnelUsers({
        ...usersQuery,
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE
      })
      .then((response) => isActual && setUsers(response.data))
      .catch(() => {
        if (!isActual) return
        setUsers(null)
        showMessage('error', 'Не удалось загрузить список пользователей', '')
      })
      .finally(() => isActual && setIsUsersLoading(false))

    return () => {
      isActual = false
    }
  }, [usersQuery, page, isUsersOpen])

  // Пока грузится сводка с роботами, под спиннером остаётся сводка без них
  const chartOverview = (includeChartBots && overviewWithBots) || overview
  const allFunnelOverview = (includeFunnelBots && overviewWithBots) || overview
  const funnelOverview = (funnelSource && sourceOverview) || allFunnelOverview
  const uniqueSourcesOverview = (includeSourceBots && overviewWithBots) || overview
  const visitSourcesOverview = (includeSourceBots && sourceVisitsWithBots) || sourceVisits

  const funnelSourceOptions = useMemo(() => {
    const rows = allFunnelOverview?.sources ?? []
    const options: { value: string; label: string; visitors?: number }[] = rows.map(
      (item) => ({ value: item.source, label: item.source, visitors: item.visitors })
    )
    // Источника может не быть за новый период — он остаётся выбранным с пустой воронкой
    if (funnelSource && !rows.some((item) => item.source === funnelSource)) {
      options.push({ value: funnelSource, label: funnelSource })
    }
    return [{ value: ALL_SOURCES, label: 'Все' }, ...options]
  }, [allFunnelOverview, funnelSource])

  const chartDays = useMemo(
    () =>
      (chartOverview?.days ?? []).map((day) => ({
        ...day,
        label: dayjs(day.date).format('DD.MM'),
        without_account: day.visitors - day.with_account
      })),
    [chartOverview]
  )

  const sources = useMemo(
    () => (isUniqueSources ? uniqueSourcesOverview : visitSourcesOverview)?.sources ?? [],
    [isUniqueSources, uniqueSourcesOverview, visitSourcesOverview]
  )

  const defaultChartSources = useMemo(
    () => sources.slice(0, DEFAULT_CHART_SOURCES).map((item) => item.source),
    [sources]
  )
  const chartSources = selectedSources ?? defaultChartSources

  const sourceChart = useMemo(
    () => buildSourceChart(sources, overview?.days ?? [], chartSources, sourceCounter(sourceStep)),
    [overview, sources, sourceStep, chartSources]
  )

  // Всегда без роботов: «Учитывать ботов» у «Источников» на этот блок не влияет
  const adLinkSources = useMemo(
    () => (sourceVisits?.sources ?? []).filter((item) => item.kind === 'ad_link'),
    [sourceVisits]
  )

  const adLinkChart = useMemo(
    () => buildSourceChart(adLinkSources, overview?.days ?? [], null, adLinkCounter),
    [overview, adLinkSources, adLinkCounter]
  )

  const onRangeChange = (value: [Dayjs | null, Dayjs | null] | null) => {
    if (value?.[0] && value[1]) {
      setRange([value[0], value[1]])
      setPage(1)
    }
  }

  const onPlatformChange = (value: TPlatformOption) => {
    setPlatform(value === 'all' ? undefined : value)
    setPage(1)
  }

  const changeStep = (nextStep: TAnalyticsFunnelStep, nextStage = stage) => {
    setStep(nextStep)
    setStage(nextStage)
    setPage(1)
  }

  const openUsers = (nextStep: TAnalyticsFunnelStep, source?: string) => {
    setUsers(null)
    setUsersSource(source)
    changeStep(nextStep, 'stopped')
    setIsUsersOpen(true)
  }

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const response = await analyticsApi.exportFunnelUsers(usersQuery)
      const href = URL.createObjectURL(new Blob([response.data]))
      const a = document.createElement('a')
      a.href = href
      const stageFile = stageOptions.find((option) => option.value === usersStage)?.file ?? ''
      const sourceFile = usersSource ? `_${usersSource.replace(/[\\/:*?"<>|\s—]+/g, '-')}` : ''
      a.download = `analytics_${step}${stageFile}${platform ? `_${platform}` : ''}${sourceFile}_${from}_${to}.xlsx`
      a.click()
      URL.revokeObjectURL(href)
    } catch {
      showMessage('error', 'Не удалось скачать список', '')
    } finally {
      setIsExporting(false)
    }
  }

  const funnel = funnelOverview?.funnel ?? []
  const humanFunnel = overview?.funnel ?? []
  const visitStats = humanFunnel.find((item) => item.step === 'visit')
  const orderStats = humanFunnel.find((item) => item.step === 'order')
  const cartStats = humanFunnel.find((item) => item.step === 'add_to_cart')
  const ordersByPlatform = chartSuperOrders
    ? overview?.totals.orders_by_platform
    : overview?.totals.seller_orders_by_platform
  const appOrders = ordersByPlatform ? ordersByPlatform.ios + ordersByPlatform.android : 0
  const hasAppVisitors = chartDays.some((day) => day.app_visitors > 0)
  const periodLabel = `${range[0].format('DD.MM.YYYY')} — ${range[1].format('DD.MM.YYYY')}`
  const platformLabel =
    platformOptions.find((option) => option.value === (platform ?? 'all'))?.label ?? ''

  const kpis = overview
    ? [
      {
        key: 'visitors',
        title: 'Посетители',
        value: formatNumber(overview.totals.visitors),
        hint: [
          `с аккаунтом ${formatNumber(overview.totals.with_account)}`,
          overview.totals.with_contact
            ? `оставили контакт ${formatNumber(overview.totals.with_contact)}`
            : '',
          `просмотров страниц и экранов ${formatNumber(overview.totals.page_views)}`
        ]
          .filter(Boolean)
          .join(' · ')
      },
      {
        key: 'conversion',
        title: 'Конверсия в заказ',
        value: formatPercent(orderStats?.visitors ?? 0, visitStats?.visitors ?? 0),
        hint: `${formatNumber(orderStats?.visitors ?? 0)} из ${formatNumber(visitStats?.visitors ?? 0)} посетителей`
      },
      {
        key: 'orders',
        title: chartSuperOrders ? 'Супер-заказы' : 'Заказы',
        value: formatNumber(chartSuperOrders ? overview.totals.orders : overview.totals.seller_orders),
        hint: `оплачено ${formatNumber(
          chartSuperOrders ? overview.totals.paid_orders : overview.totals.paid_seller_orders
        )} · покупателей ${formatNumber(overview.totals.buyers)}`,
        note: [
          `сайт ${formatNumber(ordersByPlatform?.web ?? 0)}`,
          `приложение ${formatNumber(appOrders)}`,
          ordersByPlatform?.unknown
            ? `не определено ${formatNumber(ordersByPlatform.unknown)}`
            : ''
        ]
          .filter(Boolean)
          .join(' · ')
      },
      {
        key: 'cart',
        title: 'Остановились на корзине',
        help: CART_STOPPED_HINT,
        value: typeof cartStats?.stopped === 'number' ? formatNumber(cartStats.stopped) : '—',
        hint: 'с контактами — открыть список',
        onClick: () => openUsers('add_to_cart')
      }
    ]
    : []

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <SectionTitle level={2} style={{ margin: 0 }}>
          Сквозная аналитика
        </SectionTitle>
        <div className={styles.headerControls}>
          <Select
            value={platform ?? 'all'}
            options={platformOptions}
            onChange={onPlatformChange}
            className={styles.platformSelect}
          />
          <DatePicker.RangePicker
            value={range}
            format='DD.MM.YYYY'
            presets={rangePresets}
            onChange={onRangeChange}
            disabledDate={(date) => date.isAfter(dayjs(), 'day')}
            allowClear={false}
            inputReadOnly
          />
        </div>
      </div>

      <Spin spinning={isOverviewLoading}>
        <div className={styles.kpis}>
          {kpis.map((kpi) => (
            <div
              key={kpi.key}
              className={classNames(styles.kpi, { [styles.kpiClickable]: kpi.onClick })}
              role={kpi.onClick ? 'button' : undefined}
              tabIndex={kpi.onClick ? 0 : undefined}
              onClick={kpi.onClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  kpi.onClick?.()
                }
              }}
            >
              <span className={styles.kpiTitle}>
                {kpi.title}
                {kpi.help && <Hint title={kpi.help} />}
              </span>
              <span className={styles.kpiValue}>{kpi.value}</span>
              <span className={styles.kpiHint}>{kpi.hint}</span>
              {kpi.note && <span className={styles.kpiHint}>{kpi.note}</span>}
            </div>
          ))}
        </div>

        <Spin spinning={includeChartBots && isOverviewWithBotsLoading}>
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <Typography.Title level={4} className={styles.cardTitle}>
                Заказы и посещаемость
              </Typography.Title>
              <BotsCheckbox checked={includeChartBots} onChange={setIncludeChartBots} />
              <span className={styles.titleOption}>
                <Checkbox
                  checked={chartSuperOrders}
                  onChange={(event) => setChartSuperOrders(event.target.checked)}
                >
                  Супер-заказы
                </Checkbox>
              </span>
            </div>
            <Typography.Text className={styles.cardHint}>
              График показывает только оплаченные {chartSuperOrders ? 'супер-заказы' : 'заказы'}
            </Typography.Text>
            <div className={styles.chart}>
              <ResponsiveContainer width='100%' height='100%'>
                <ComposedChart data={chartDays} margin={{ top: 8, right: 4, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke='#dfe8f5' vertical={false} />
                  <XAxis
                    dataKey='label'
                    interval='preserveStartEnd'
                    minTickGap={24}
                    tick={{ fill: '#637aa0', fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId='orders'
                    allowDecimals={false}
                    width={34}
                    tick={{ fill: '#637aa0', fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId='visitors'
                    orientation='right'
                    allowDecimals={false}
                    width={46}
                    tick={{ fill: '#637aa0', fontSize: 12 }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => (
                      <DayTooltip
                        active={active}
                        day={payload?.[0]?.payload as TChartDay | undefined}
                        label={label}
                        showApp={hasAppVisitors}
                        superOrders={chartSuperOrders}
                      />
                    )}
                  />
                  <Legend itemSorter={(item) => dayChartOrder.indexOf(String(item.dataKey))} />
                  <Bar
                    yAxisId='visitors'
                    stackId='visitors'
                    dataKey='with_account'
                    name='С аккаунтом'
                    fill='#FF9433'
                  />
                  <Bar
                    yAxisId='visitors'
                    stackId='visitors'
                    dataKey='without_account'
                    name='Посетители'
                    fill='#d0d7e2'
                  />
                  <Line
                    yAxisId='orders'
                    dataKey={chartSuperOrders ? 'paid_orders' : 'paid_seller_orders'}
                    name={chartSuperOrders ? 'Супер-заказы' : 'Заказы'}
                    type='monotone'
                    stroke='#2f75ff'
                    strokeWidth={3}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Spin>

        <Spin
          spinning={
            (includeFunnelBots && isOverviewWithBotsLoading) ||
            (Boolean(funnelSource) && isSourceOverviewLoading)
          }
        >
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <Typography.Title level={4} className={styles.cardTitle}>
                Воронка
              </Typography.Title>
              <Select
                value={funnelSource ?? ALL_SOURCES}
                options={funnelSourceOptions}
                onChange={(value: string) => setFunnelSource(value || undefined)}
                showSearch
                optionFilterProp='label'
                popupMatchSelectWidth={false}
                optionRender={(option) => (
                  <span className={styles.sourceOption}>
                    {option.label}
                    {typeof option.data.visitors === 'number' && (
                      <span className={styles.muted}>{formatNumber(option.data.visitors)}</span>
                    )}
                  </span>
                )}
                className={styles.funnelSourceSelect}
              />
              <BotsCheckbox checked={includeFunnelBots} onChange={setIncludeFunnelBots} />
            </div>
            <Typography.Text className={styles.cardHint}>
              Нажмите на шаг, чтобы открыть список людей с контактами, которые до него дошли
            </Typography.Text>
            <div className={styles.funnel}>
              {funnel.map((item, index) => {
                const first = funnel[0]?.visitors ?? 0
                const previous = funnel[index - 1]?.visitors ?? 0
                return (
                  <button
                    key={item.step}
                    type='button'
                    className={styles.funnelStep}
                    onClick={() => openUsers(item.step, funnelSource)}
                  >
                    <span className={styles.funnelHead}>
                      <span>{stepLabels[item.step]}</span>
                      <strong>{formatNumber(item.visitors)}</strong>
                    </span>
                    <span className={styles.funnelTrack}>
                      <span
                        className={styles.funnelBar}
                        style={{ width: `${first ? (item.visitors / first) * 100 : 0}%` }}
                      />
                    </span>
                    <span className={styles.funnelMeta}>
                      {index > 0 && (
                        <span>{formatPercent(item.visitors, previous)} от предыдущего</span>
                      )}
                      {index > 1 && (
                        <span>{formatPercent(item.visitors, first)} от зашедших</span>
                      )}
                      <span>{stepBreakdown(item)}</span>
                      {typeof item.stopped === 'number' && (
                        <span>
                          остановились {formatNumber(item.stopped)} из{' '}
                          {formatNumber(item.with_account + item.with_contact)} с контактами
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </Spin>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <Typography.Title level={4} className={styles.cardTitle}>
              Источники
            </Typography.Title>
            <BotsCheckbox checked={includeSourceBots} onChange={setIncludeSourceBots} />
            <Checkbox
              className={styles.titleOption}
              checked={isUniqueSources}
              onChange={(event) => setIsUniqueSources(event.target.checked)}
            >
              Только уникальные (за период)
            </Checkbox>
          </div>
          <ul className={styles.sourceHint}>
            {(isUniqueSources ? uniqueSourcesHint : visitSourcesHint).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <Spin
            spinning={
              isUniqueSources
                ? includeSourceBots && isOverviewWithBotsLoading
                : isSourceVisitsLoading || (includeSourceBots && isSourceVisitsWithBotsLoading)
            }
          >
            <div className={styles.sourceControls}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: stepMenuItems,
                  selectable: true,
                  selectedKeys: [sourceStep],
                  onClick: ({ key }) => setSourceStep(key as TAnalyticsFunnelStep)
                }}
              >
                <Button>
                  {stepLabels[sourceStep]} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                trigger={['click']}
                dropdownRender={() => (
                  <div className={styles.sourcePicker}>
                    <Checkbox.Group
                      value={chartSources}
                      options={sources.map((item) => ({
                        value: item.source,
                        label: (
                          <span className={styles.sourceOption}>
                            {item.source}
                            <span className={styles.muted}>
                              {formatNumber(item[sourceCounter(sourceStep)])}
                            </span>
                          </span>
                        )
                      }))}
                      onChange={(values) => setSelectedSources(values as string[])}
                    />
                    <Button type='link' size='small' onClick={() => setSelectedSources(null)}>
                      Первые {DEFAULT_CHART_SOURCES} по трафику
                    </Button>
                  </div>
                )}
              >
                <Button>
                  Источники на графике: {sourceChart.series.length} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: sourceChartTypeItems,
                  selectable: true,
                  selectedKeys: [sourceChartType],
                  onClick: ({ key }) => setSourceChartType(key as TSourceChartType)
                }}
              >
                <Button>
                  Вид диаграммы: {sourceChartTypeLabels[sourceChartType]} <DownOutlined />
                </Button>
              </Dropdown>
            </div>
            <SourceChart chart={sourceChart} type={sourceChartType} />
            <Table
              rowKey='source'
              size='small'
              columns={sourceColumns}
              dataSource={sources}
              showSorterTooltip={false}
              pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
            />
          </Spin>
        </div>

        <div className={styles.card}>
          <Typography.Title level={4} className={styles.cardTitle}>
            Рекламные ссылки
          </Typography.Title>
          <ul className={styles.sourceHint}>
            {adLinksHint.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <Spin spinning={isSourceVisitsLoading}>
            <div className={styles.sourceControls}>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: adLinkCounterItems,
                  selectable: true,
                  selectedKeys: [adLinkCounter],
                  onClick: ({ key }) => setAdLinkCounter(key as TSourceCounter)
                }}
              >
                <Button>
                  {adLinkCounterLabels[adLinkCounter]} <DownOutlined />
                </Button>
              </Dropdown>
              <Dropdown
                trigger={['click']}
                menu={{
                  items: sourceChartTypeItems,
                  selectable: true,
                  selectedKeys: [adLinkChartType],
                  onClick: ({ key }) => setAdLinkChartType(key as TSourceChartType)
                }}
              >
                <Button>
                  Вид диаграммы: {sourceChartTypeLabels[adLinkChartType]} <DownOutlined />
                </Button>
              </Dropdown>
            </div>
            <SourceChart chart={adLinkChart} type={adLinkChartType} />
            <Table
              rowKey='source'
              size='small'
              columns={adLinkColumns}
              dataSource={adLinkSources}
              showSorterTooltip={false}
              pagination={{ pageSize: 10, showSizeChanger: false, hideOnSinglePage: true }}
              locale={{
                emptyText: (
                  <>
                    За период входов по ссылкам нет. Ссылки создаются в разделе{' '}
                    <Link to={`${superAdminRoutes.ACTIONS}/${superAdminRoutes.ACTIONS_AD_LINKS}`}>
                      «Рекламные ссылки»
                    </Link>
                  </>
                )
              }}
            />
          </Spin>
        </div>

        <div className={styles.card}>
          <Typography.Title level={4} className={styles.cardTitle}>
            Платформы
          </Typography.Title>
          <Typography.Text className={styles.cardHint}>
            Кто заходил с нескольких платформ, считается в каждой своей строке
            {ordersByPlatform?.unknown
              ? `. Ещё ${formatNumber(ordersByPlatform.unknown)} ${getDeclination(
                ordersByPlatform.unknown % 100,
                chartSuperOrders
                  ? ['супер-заказов', 'супер-заказ', 'супер-заказа']
                  : ['заказов', 'заказ', 'заказа']
              )} без платформы`
              : ''}
          </Typography.Text>
          <Table
            rowKey='platform'
            size='small'
            columns={getPlatformColumns(
              chartSuperOrders,
              sourceVisits?.platforms,
              isSourceVisitsLoading
            )}
            dataSource={overview?.platforms ?? []}
            pagination={false}
          />
        </div>
      </Spin>

      <div className={styles.notes}>
        <Typography.Title level={5}>Как считаются цифры</Typography.Title>
        <ul>
          <li>
            Считаем сами по действиям на сайте и в приложении, без Яндекс Метрики: у
            каждого человека видно, откуда он пришёл, что смотрел и клал в корзину и
            сделал ли заказ.
          </li>
          <li>
            Контакты есть у тех, кто вошёл в аккаунт, и у гостей, которые оставили телефон
            или почту с согласием на обработку персональных данных — при оформлении заказа
            или входе по коду. Без согласия контакт гостя не сохраняется.
          </li>
          <li>
            Посетитель без согласия на cookie анонимный и каждые сутки считается заново,
            поэтому за длинный период посетителей больше, чем людей.
          </li>
          <li>
            Роботов по умолчанию нет ни в одной цифре. Робот — программа, которая сама
            называет себя роботом: поисковики, проверки сайта, скрипты. Робот, который
            выдаёт себя за обычный браузер, считается человеком — так правило не задевает
            людей. «Учитывать ботов» добавляет их в график, воронку или источники; карточки
            вверху и «Платформы» всегда без роботов.
          </li>
          <li>
            Шаг засчитан, если человек дошёл до него или дальше: корзину могли собрать до
            периода или на другом устройстве, а оформить заказ — в этом периоде.
          </li>
          <li>
            «Оформили заказ» — любой заказ за период, «Оплатили заказ» — заказ, по
            которому прошла оплата, даже если его потом отменили или вернули. Так же
            считается «оплачено» в карточке заказов.
          </li>
          <li>
            Супер-заказ — одна оформленная корзина, заказ — её часть у одного продавца:
            корзина у трёх продавцов — один супер-заказ и три заказа. Галочка
            «Супер-заказы» у графика переключает и карточку вверху, и «Платформы».
          </li>
          <li>
            «Сайт (desktop)» — компьютер, «Сайт (mobile)» — телефон или планшет: это
            называет браузер. iPad с iPadOS 13 и новее называет себя компьютером и попадает в
            desktop. Если устройство не известно — «Сайт (не определено)». Фильтр «Сайт»
            берёт все три строки сайта.
          </li>
          <li>
            «Входы» в «Платформах» считаются как в «Источниках» по входам, но по
            платформе, с которой вход начался. Поэтому у приложения входов бывает чуть
            больше, чем в строках «Приложение iOS» и «Приложение Android» в источниках:
            вход в приложение по рекламной ссылке или с другого сайта там записан на эту
            рекламу или сайт.
          </li>
          <li>
            В «Источниках» по входам источник входа на сайт — метки в адресе, с которого
            вход начался, или сайт, откуда пришли. Человек, пришедший с рекламы и
            вернувшийся сам, во второй раз — прямой заход. В уникальных источниках человек
            считается один раз — по первому источнику за период.
          </li>
          <li>
            Рекламные ссылки создаются в разделе «Рекламные ссылки». В источниках они
            называются по типу и названию: «Блогер (…)», «Партнёр (…)», «Офлайн (…)».
          </li>
          <li>
            Заказы продавцов с «Исключить из аналитики» в настройках продавца здесь не
            считаются, как и в статистике заказов площадки. Если в корзине был и другой
            продавец, заказ считается целиком.
          </li>
          <li>
            На графике источников человек считается один раз — в день, когда впервые за
            период дошёл до выбранного шага, поэтому сумма по дням равна строке таблицы.
            На графике посещаемости он считается в каждый день, когда заходил.
          </li>
          <li>
            Старые версии приложения действий не присылают, поэтому их покупатели попадают
            в воронку только по заказу — с источником «Приложение» или «Только заказ»,
            если платформа заказа не записана.
          </li>
          <li>
            Заказы без записанной платформы — в «не определено» и в фильтр по платформе
            не попадают.
          </li>
          <li>
            Рекламу присылать можно только тем, у кого в колонке «Рассылка» стоит
            «согласен». Каждая выгрузка записывается в журнал.
          </li>
        </ul>
      </div>

      <Drawer
        open={isUsersOpen}
        onClose={() => setIsUsersOpen(false)}
        width='100%'
        title='Пользователи'
        destroyOnClose
        rootClassName={styles.usersDrawer}
      >
        <div className={styles.usersHeader}>
          <div className={styles.usersControls}>
            <Select
              value={step}
              options={stepOptions}
              onChange={(value) => changeStep(value)}
              className={styles.stepSelect}
            />
            <div className={styles.stages}>
              {stageOptions
                .filter((option) => !isLastStep || option.value === 'all')
                .map((option) => {
                  const count = users?.[option.value]
                  return (
                    <button
                      key={option.value}
                      type='button'
                      aria-pressed={usersStage === option.value}
                      className={classNames(styles.stage, option.className, {
                        [styles.stageActive]: usersStage === option.value
                      })}
                      onClick={() => {
                        setStage(option.value)
                        setPage(1)
                      }}
                    >
                      {option.label}
                      {typeof count === 'number' && ` (${formatNumber(count)})`}
                    </button>
                  )
                })}
              {!isLastStep && <Hint title={STAGES_HINT} />}
            </div>
            <span className={styles.titleOption}>
              <Checkbox
                checked={showAnonymous}
                onChange={(event) => {
                  setShowAnonymous(event.target.checked)
                  setPage(1)
                }}
              >
                Показывать анонимных
              </Checkbox>
              <Hint title={ANONYMOUS_HINT} />
            </span>
          </div>
          <Button
            icon={<DownloadOutlined />}
            loading={isExporting}
            disabled={!users?.total}
            onClick={handleExport}
          >
            Скачать .xlsx
          </Button>
        </div>
        <Typography.Text className={styles.cardHint}>
          {periodLabel} · {platformLabel}
          {usersSource ? ` · источник «${usersSource}»` : ''}
          {users ? `. ${usersSummary(users, usersStage, showAnonymous)}` : ''}
          {' '}Нажмите на строку, чтобы увидеть подробности.
        </Typography.Text>
        <Table
          rowKey={(user) => `${user.kind}-${user.user_id}`}
          size='small'
          loading={isUsersLoading}
          columns={userColumns}
          dataSource={users?.items ?? []}
          rowClassName={(user) =>
            classNames(styles.userRow, {
              [styles.userRowPassed]: usersStage === 'all' && user.passed === true,
              [styles.userRowStopped]: usersStage === 'all' && user.passed === false
            })
          }
          expandable={{
            expandRowByClick: true,
            expandedRowRender: (user) => renderUserDetails(user, step)
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: users?.total ?? 0,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: setPage
          }}
          scroll={{ x: 1360 }}
        />
      </Drawer>
    </div>
  )
}
